import window from 'window-or-global';
import { isDarkFeatureEnabled } from '../utils/dark-features-query-selector';
import { bind } from '@confluence-classic/confluence-event_DO_NOT_USE';
// eslint-disable-next-line no-restricted-imports
import $ from 'jquery';

// firefox and IE11 use a mutation observer to detect changes to the DOM and grab the img node that is inserted into the content editable
const registerMutationObserver = function (ed) {
	const observer = new MutationObserver((mutations) => {
		for (let i = 0; i < mutations.length; i++) {
			const mutation = mutations[i];

			if (mutation.addedNodes.length) {
				for (let x = 0; x < mutation.addedNodes.length; x++) {
					const node = mutation.addedNodes[x];

					if (node.localName === 'img') {
						window.setTimeout(pasteHandler.pasteFromDataURL, 0);
						return;
					}
				}
			}
		}
	});
	observer.observe(ed.getDoc(), { childList: true, subtree: true });
};

// chrome uses a paste handler and grabs the image data out of the paste event
const registerOnPasteListener = function (ed) {
	ed.on('Paste', (e) => {
		if (pasteHandler.onPaste(e)) {
			return true;
		} else {
			window.tinymce.dom.Event.cancel(e);
			return false;
		}
	});
};

let pasteHandler = {
	initializeEditor() {
		if (window.AJS && window.AJS.Rte && window.AJS.Rte.BootstrapManager) {
			window.AJS.Rte.BootstrapManager.addOnInitCallback(() => {
				const ed = window.tinymce.activeEditor;

				if (ed) {
					// Register observer to listen for img nodes inserted into the DOM. IE still doesn't
					// expose enough information to grab the image from the clipboard
					if (window.MutationObserver && !(window.tinymce.Env.gecko || window.tinymce.Env.webkit)) {
						registerMutationObserver(ed);
					} else {
						registerOnPasteListener(ed);
					}
				} else if (process.env.NODE_ENV !== 'production') {
					console.error('Could not getEditor from adapter');
				}
			});
		} else if (process.env.NODE_ENV !== 'production') {
			console.error('Could not initialize pasteHandler on Confluence.Editor');
		}
	},

	/**
	 * searches the wysiwyg text area for embedded image data URLs and attaches them to the document
	 * via the upload, then removes the data URI
	 */
	pasteFromDataURL() {
		//firefox 4.0 inserts images as data urls
		//do not count the images have data url built by macro placeholder in editor with editor-inline-macro class
		const embeddedImgs = $(
			'img[src^="data:image"]:not(".editor-inline-macro")',
			window.tinymce.activeEditor.getDoc(),
		);
		$.each(embeddedImgs, function () {
			const imgNode = this;
			const dataUrl = imgNode.src;

			let data;
			let size;
			let encode;
			// Upload binary data if using FileStore direct upload
			if (
				isDarkFeatureEnabled('filestore.direct.upload') &&
				// Make it easier to disable "filestore.direct.upload" using UI. Will be removed soon.
				!isDarkFeatureEnabled('filestore.direct.upload.force.disable')
			) {
				const arr = dataUrl.split(','); // get base64 encoded data
				const bstr = atob(arr[1]); // get binary data from base64 encoded data
				let n = bstr.length;

				size = bstr.length;
				data = new Uint8Array(n);
				while (n--) {
					data[n] = bstr.charCodeAt(n);
				}
			} else {
				// Otherwise upload base64 encoded data
				encode = 'base64';
				data = dataUrl.substring(22, dataUrl.length);

				// calculate the unencoded image size, accounting for potential padding.
				size = (data.length * 3) / 4;
				//count padded bytes, last two characters might be padding with the '=' char
				let paddedBytes = 0;
				for (let len = data.length, i = len - 1; data[i] == '='; i--) {
					paddedBytes++;
				}

				size = size - paddedBytes;
			}

			window.AJS.Confluence.Uploader.upload([pasteHandler.createUploadFile(data, size, encode)]);
		});

		//remove all embedded images
		embeddedImgs.remove();
	},

	/**
	 * onPaste handler, Either uses the clipboardData object on the paste event, or interprets
	 * the data uri that has been embedded in the page.
	 */
	onPaste(event) {
		if (event && event.clipboardData) {
			// webkit exposes a clipboardData object on the paste event
			return pasteHandler.pasteFromEvent(event);
		}
		return true;
	},

	showClipboardAccessError() {
		const ed = window.tinymce.activeEditor;

		new window.AJS.Dialog({
			width: 400,
			closeOnOutsideClick: true,
		})
			.addPanel(
				'alert',
				$('<div />', {
					text: ed.translate('clipboardaccess.browser.not.supported'),
				}),
			)
			.addButton(ed.translate('clipboardaccess.ok'), (dialog) => {
				dialog.remove();
			})
			.show()
			.updateHeight();
	},

	/**
	 * chrome supports clipboard data on the paste event, can read the image straight from it.
	 * @param event
	 */
	pasteFromEvent(event) {
		if (event && event.clipboardData) {
			if (event.clipboardData.items) {
				const dataTransferItem = event.clipboardData.items[0];
				if (!dataTransferItem || dataTransferItem.kind != 'file') {
					return true;
				}

				const dataFile = dataTransferItem.getAsFile();
				const uploadFile = pasteHandler.createUploadFile(dataFile, dataFile.size);
				window.AJS.Confluence.Uploader.upload([uploadFile]);
				return false;
			} else {
				// Safari (and possibly other browsers) will
				// allow the image to be pasted, but the data
				// URI is not placed as the image's `src`
				// attribute. It replaces it with a custom
				// protocol, which can't be accessed via JavaScript.
				window.setTimeout(
					(me) => {
						const webkitFakeUrlImages = $(
							"img[src^='webkit-fake-url://']",
							window.tinymce.activeEditor.getDoc(),
						);
						if (webkitFakeUrlImages.length) {
							webkitFakeUrlImages.remove();
							me.showClipboardAccessError();
						}
					},
					0,
					this,
				);
			}
		}
		return true;
	},

	/**
	 * creates an upload file with an optionally specified encoding
	 * @param data - the data to be uploaded, either as binary or base64 encoded
	 * @param sz - the size of the decoded image
	 * @param encoding - optional encoding, only base64 is currently supported.
	 */
	createUploadFile(data, sz, encoding) {
		const curDate = new Date();
		const month = curDate.getMonth() + 1;
		return {
			fileName: `image${curDate.getFullYear()}-${month}-${curDate.getDate()}_${curDate.getHours()}-${curDate.getMinutes()}-${curDate.getSeconds()}.png`,
			fileSize: sz,
			slice(offset, length) {
				return data.slice(offset, offset + length);
			},
			getAsBinary() {
				return data;
			},
			getData() {
				return data;
			},
			encoding,
		};
	},
};

export default function clipboardAccess() {
	bind('rte-ready', pasteHandler.initializeEditor);
}
